// Single source of truth for how a Supplements bullet encodes
//   "Name <separator> Description [ — https://link]"
// The pull-time enricher (scripts/applySupplementLinks.js) writes the
// name/description with one of these separators and appends the " — <url>"
// tail; the renderers parse it back to make the supplement name clickable.
//
// IMPORTANT: keep SUPPLEMENT_ITEM_SEPARATORS identical to
// NAME_DESCRIPTION_SEPARATORS in scripts/applySupplementLinks.js. When the
// writer and a reader disagree on which separators count, the name fails to
// parse and the link is silently dropped — the immune supplements regressed
// exactly this way after the sheet switched from double- to single-space
// em-dashes. Both frontend renderers now import from here so they can't drift
// from each other again.
//
// The bare hyphen is tried LAST so a hyphenated name like "P - Pancreas — …"
// extracts the full name before any fallback to splitting on the hyphen.
export const SUPPLEMENT_ITEM_SEPARATORS = ['  —  ', ' — ', ':  ', ' - '];

// Matches a trailing "  —  https://…" or " — https://…" tail. The enricher
// appends the URL with a single-space em-dash; the regex tolerates any
// whitespace so manually edited override text still parses.
export const SUPPLEMENT_TRAILING_URL_RE = /\s+—\s+(https?:\/\/\S+)\s*$/;

export type ParsedSupplement = {
  name: string;
  description: string;
  link?: string;
};

// Peel the trailing URL (if any) off a Supplements bullet. Returns the text
// without the URL and the extracted link. Used both by the structured parser
// below and by callers that want to render an unstructured line (e.g. a promo
// line) without leaking the raw URL as text.
export function stripSupplementUrl(item: string): {
  text: string;
  link?: string;
} {
  const urlMatch = SUPPLEMENT_TRAILING_URL_RE.exec(item);
  if (!urlMatch) return { text: item };
  return {
    text: item.slice(0, urlMatch.index).trimEnd(),
    link: urlMatch[1],
  };
}

// Parse "Name <separator> Description [ — url]" into its parts. Returns null
// when the bullet has no name/description separator (e.g. the "Use code …"
// promo line), so callers can fall back to rendering it as plain text.
export function parseSupplementItem(item: string): ParsedSupplement | null {
  const { text: working, link } = stripSupplementUrl(item);
  for (const sep of SUPPLEMENT_ITEM_SEPARATORS) {
    const idx = working.indexOf(sep);
    if (idx <= 0) continue;
    const name = working.slice(0, idx).trim();
    const description = working.slice(idx + sep.length).trim();
    if (name && description) return { name, description, link };
  }
  return null;
}

// Fallback Systemic Formulas product page for a product whose real URL isn't in
// the supplement catalog. The preferred source is always the catalog link
// (FurtherConsiderations.supplementLinkByName / a resolved tag's own link),
// which carries the sheet's exact URL; this slug guess only runs when a product
// hasn't been wired up yet, so a product name is never dead text.
//
// The micro sign is URL-encoded to %c2%b5 (not folded to "u") to match the
// site's own scheme — e.g. "Terra µBiomic" → terra-%c2%b5biomic.
//
// Systemic Formulas ONLY: callers must not apply it to another brand's product,
// which would link somewhere that doesn't sell it.
export function systemicFormulasProductUrl(name: string): string {
  return `https://systemicformulas.com/product/${name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[µμ]/g, '%c2%b5')
    .replace(/[^a-z0-9%-]+/g, '-')
    .replace(/(^-|-$)/g, '')}/`;
}
